/**
 * Centralized Frontend Logger Service
 * 
 * Replaces all console.log/warn/error/info/debug calls in the frontend.
 * 
 * Behavior:
 * - ALWAYS silent in the browser console (both dev and production)
 * - Buffers log entries and periodically sends them to the backend
 * - Backend writes logs to daily rotating files: backend/logs/frontend_YYYY-MM-DD.log
 * 
 * Usage:
 *   import logger from '@/services/logger'
 *   logger.log('Some message', someData)
 *   logger.warn('Warning message')
 *   logger.error('Error occurred', error)
 */

import { API_BASE_URL } from '@/config/api';

class Logger {
  constructor() {
    this._buffer = [];
    this._flushInterval = 5000; // Flush every 5 seconds
    this._maxBufferSize = 100;  // Force flush if buffer exceeds this
    this._timer = null;
    this._isFlushing = false;

    // Start the periodic flush timer
    this._startFlushTimer();

    // Flush on page unload to capture final logs
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this._flush());
    }
  }

  /**
   * Log at INFO level
   */
  log(source, ...args) {
    this._addEntry('INFO', source, args);
  }

  /**
   * Log at WARN level
   */
  warn(source, ...args) {
    this._addEntry('WARN', source, args);
  }

  /**
   * Log at ERROR level
   */
  error(source, ...args) {
    this._addEntry('ERROR', source, args);
  }

  /**
   * Log at INFO level (alias)
   */
  info(source, ...args) {
    this._addEntry('INFO', source, args);
  }

  /**
   * Log at DEBUG level
   */
  debug(source, ...args) {
    this._addEntry('DEBUG', source, args);
  }

  /**
   * Internal: add a log entry to the buffer
   */
  _addEntry(level, source, args) {
    const entry = {
      timestamp: new Date().toISOString(),
      level,
      source: source || 'unknown',
      message: this._formatArgs(args)
    };

    this._buffer.push(entry);

    // Force flush if buffer is too large
    if (this._buffer.length >= this._maxBufferSize) {
      this._flush();
    }
  }

  /**
   * Internal: format arguments into a single string message
   */
  _formatArgs(args) {
    if (!args || args.length === 0) return '';

    return args.map(arg => {
      if (arg === null) return 'null';
      if (arg === undefined) return 'undefined';
      if (arg instanceof Error) {
        return `${arg.name}: ${arg.message}${arg.stack ? '\n' + arg.stack : ''}`;
      }
      if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg, null, 2);
        } catch {
          return String(arg);
        }
      }
      return String(arg);
    }).join(' ');
  }

  /**
   * Internal: start periodic flush timer
   */
  _startFlushTimer() {
    if (this._timer) return;
    this._timer = setInterval(() => this._flush(), this._flushInterval);
  }

  /**
   * Internal: flush buffered logs to backend
   */
  async _flush() {
    if (this._isFlushing || this._buffer.length === 0) return;

    this._isFlushing = true;
    const entries = this._buffer.splice(0); // Take all entries

    try {
      const url = `${API_BASE_URL}/api/frontend-logs`;
      
      // Use navigator.sendBeacon for beforeunload, fetch otherwise
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        // Try sendBeacon first (works during page unload)
        const blob = new Blob([JSON.stringify({ logs: entries })], {
          type: 'application/json'
        });
        const sent = navigator.sendBeacon(url, blob);
        if (!sent) {
          // Fallback to fetch
          await this._fetchSend(url, entries);
        }
      } else {
        await this._fetchSend(url, entries);
      }
    } catch {
      // If sending fails, put entries back at the front of the buffer
      // but only keep a reasonable number to avoid memory issues
      const remaining = entries.slice(-50);
      this._buffer.unshift(...remaining);
    } finally {
      this._isFlushing = false;
    }
  }

  /**
   * Internal: send logs via fetch API
   */
  async _fetchSend(url, entries) {
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ logs: entries }),
      // Don't send credentials/JWT for logging - it's a fire-and-forget endpoint
      keepalive: true
    });
  }
}

// Export singleton instance
const logger = new Logger();
export default logger;
